import React, { useContext, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { I18n } from "aws-amplify/utils";
import { Badge } from "primereact/badge";
import { Dialog } from "primereact/dialog";
import { Button } from "primereact/button";
import { UserContext } from "../../app-context";
import { getNotifications, markNotificationsRead, deleteNotifications } from "../../service/NotificationService";
import './NotificationsPanel.css';

function notificationMessage(notification) {
    const date = notification?.payload?.date ?? "";
    const key = `notifications.${notification?.type ?? "unknown"}`;
    return I18n.get(key).replace("{{date}}", date);
}

function notificationLink(notification) {
    return notification?.link || null;
}

export const NotificationsPanel = () => {
    const authenticatedUserData = useContext(UserContext);
    const navigate = useNavigate();
    const [notifications, setNotifications] = useState([]);
    const [dialogVisible, setDialogVisible] = useState(false);
    // Distingue une ouverture automatique (au chargement, s'il y a des non-lues) d'une ouverture
    // demandée par l'utilisateur (clic sur la cloche) : dans le premier cas on n'y déplace pas le
    // focus et on ne bloque pas le reste de la page (overlay non modal), pour ne pas provoquer de
    // changement de contexte inattendu (WCAG 2.4.3/3.2.5) ; dans le second, comportement modal standard.
    const [autoOpened, setAutoOpened] = useState(false);
    const autoOpenTriggeredRef = useRef(false);

    const userSub = authenticatedUserData?.userSub;
    const unreadNotifications = notifications.filter((notification) => notification.isRead !== true);
    const unreadCount = unreadNotifications.length;
    const readCount = notifications.length - unreadCount;
    const notificationsButtonLabel = unreadCount > 0
        ? `${I18n.get("notifications.title")} - ${I18n.get("notifications.unreadCount").replace("{{count}}", unreadCount)}`
        : I18n.get("notifications.title");

    useEffect(() => {
        if (!userSub) {
            return;
        }

        getNotifications((loaded) => {
            setNotifications(loaded);
            if (!autoOpenTriggeredRef.current && loaded.some((notification) => notification.isRead !== true)) {
                autoOpenTriggeredRef.current = true;
                setAutoOpened(true);
                setDialogVisible(true);
            }
        });
    }, [userSub]);

    const openDialog = (event) => {
        if (event) {
            event.preventDefault();
            event.stopPropagation();
        }
        setAutoOpened(false);
        setDialogVisible(true);
    };

    const closeDialog = () => {
        setDialogVisible(false);
    };

    const markAllRead = () => {
        markNotificationsRead({ all: true }, (err) => {
            if (!err) {
                setNotifications((current) => current.map((notification) => ({ ...notification, isRead: true })));
            }
        });
    };

    const markOneRead = (notificationId) => {
        markNotificationsRead({ notificationIds: [notificationId] }, (err) => {
            if (!err) {
                setNotifications((current) => current.map((item) =>
                    item.notificationId === notificationId ? { ...item, isRead: true } : item
                ));
            }
        });
    };

    const onNotificationClick = (notification) => {
        if (notification.isRead !== true) {
            markOneRead(notification.notificationId);
        }

        const link = notificationLink(notification);
        if (link) {
            setDialogVisible(false);
            navigate(link);
        }
    };

    const onMarkOneReadClick = (event, notification) => {
        event.preventDefault();
        event.stopPropagation();
        markOneRead(notification.notificationId);
    };

    const deleteOne = (notificationId) => {
        deleteNotifications({ notificationIds: [notificationId] }, (err) => {
            if (!err) {
                setNotifications((current) => current.filter((item) => item.notificationId !== notificationId));
            }
        });
    };

    const onDeleteOneClick = (event, notification) => {
        event.preventDefault();
        event.stopPropagation();
        if (window.confirm(I18n.get("notifications.confirmDelete"))) {
            deleteOne(notification.notificationId);
        }
    };

    const deleteAllRead = () => {
        if (!window.confirm(I18n.get("notifications.confirmDeleteAllRead"))) {
            return;
        }
        deleteNotifications({ allRead: true }, (err) => {
            if (!err) {
                setNotifications((current) => current.filter((notification) => notification.isRead !== true));
            }
        });
    };

    const dialogFooter = (
        <div>
            {unreadCount > 0 && (
                <Button
                    label={I18n.get("notifications.markAllRead")}
                    className="p-button-outlined p-button-secondary"
                    onClick={markAllRead}
                />
            )}
            {readCount > 0 && (
                <Button
                    label={I18n.get("notifications.deleteAllRead")}
                    className="p-button-outlined p-button-danger"
                    onClick={deleteAllRead}
                />
            )}
            <Button
                label={I18n.get("button.close")}
                onClick={closeDialog}
            />
        </div>
    );

    return (
        <>
            <li>
                <button
                    type="button"
                    className="p-link layout-topbar-button"
                    title={notificationsButtonLabel}
                    aria-label={notificationsButtonLabel}
                    onClick={openDialog}
                >
                    <i className="pi pi-fw pi-bell p-text-secondary p-overlay-badge" aria-hidden="true">
                        {unreadCount > 0 && <Badge value={unreadCount}></Badge>}
                    </i>
                </button>
                <div className="sr-only" role="status" aria-live="polite">
                    {unreadCount > 0 ? I18n.get("notifications.unreadCount").replace("{{count}}", unreadCount) : ""}
                </div>
            </li>
            <Dialog
                header={I18n.get("notifications.title")}
                visible={dialogVisible}
                modal={!autoOpened}
                focusOnShow={!autoOpened}
                className="notifications-dialog"
                style={{ width: '32rem' }}
                footer={dialogFooter}
                onHide={closeDialog}
                appendTo={document.body}
            >
                {notifications.length === 0 ? (
                    <p className="notifications-empty">{I18n.get("notifications.empty")}</p>
                ) : (
                    <ul className="notifications-list">
                        {notifications.map((notification) => {
                            const link = notificationLink(notification);
                            return (
                            <li
                                key={notification.notificationId}
                                className={notification.isRead === true ? "notification-item notification-item--read" : "notification-item"}
                            >
                                <div className="notification-item-row">
                                    <button
                                        type="button"
                                        className={link ? "notification-item-button notification-item-button--link" : "notification-item-button"}
                                        onClick={() => onNotificationClick(notification)}
                                    >
                                        <span className="sr-only">
                                            {notification.isRead === true ? I18n.get("notifications.statusRead") : I18n.get("notifications.statusUnread")}
                                        </span>
                                        <span className="notification-item-message">{notificationMessage(notification)}</span>
                                        {notification.createdAt && (
                                            <span className="notification-item-date">
                                                {new Date(notification.createdAt).toLocaleString(I18n.get("language") === "de" ? "de-LU" : "fr-LU")}
                                            </span>
                                        )}
                                    </button>
                                    {notification.isRead !== true && (
                                        <button
                                            type="button"
                                            className="notification-item-mark-read"
                                            title={I18n.get("notifications.markRead")}
                                            aria-label={I18n.get("notifications.markRead")}
                                            onClick={(event) => onMarkOneReadClick(event, notification)}
                                        >
                                            <i className="pi pi-check" aria-hidden="true"></i>
                                        </button>
                                    )}
                                    <button
                                        type="button"
                                        className="notification-item-delete"
                                        title={I18n.get("notifications.delete")}
                                        aria-label={I18n.get("notifications.delete")}
                                        onClick={(event) => onDeleteOneClick(event, notification)}
                                    >
                                        <i className="pi pi-trash" aria-hidden="true"></i>
                                    </button>
                                </div>
                            </li>
                            );
                        })}
                    </ul>
                )}
            </Dialog>
        </>
    );
};
